if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      /**
       * Custom project notes (2026-04-09):
       * 1) Variant change now updates both price containers:
       *    - #price-<sectionId>
       *    - #price-buy-button-<sectionId>
       *    This also applies to unavailable-state hide logic.
       * 2) Focus restore after variant fetch uses preventScroll to avoid jump:
       *    document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
       * 3) Variant-driven media activation calls media-gallery with:
       *    { scrollIntoView: false }
       *    to prevent automatic page vertical scrolling on variant switch.
       */
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      skuInputSyncBound = false;
      skuInputSyncHandler = null;
      skuInputSyncDebounce = null;
      skuInputSyncScanDebounce = null;
      skuInputSyncObserver = null;
      skuToVariantIdMap = null;
      pendingSkuVariantSwitchId = null;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initSkuInputSync();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.teardownSkuInputSync();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        return fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value without forcing page scroll
            if (targetId) {
              document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
            }
          })
          .catch((error) => {
            if (error?.name === 'AbortError') {
              return null;
            }
            console.error(error);
            return null;
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantMetadata(variant);
          this.pendingSkuVariantSwitchId = null;

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('price-buy-button');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('SkuGtin', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('SkuGtinFoldable', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateVariantMetadata(variant) {
        if (!variant) return;

        this.dataset.variantId = variant.id ?? '';
        this.dataset.variantSku = variant.sku ?? '';
      }

      initSkuInputSync() {
        if (this.skuInputSyncBound) return;

        this.skuInputSyncHandler = (event) => {
          const node = event.target?.closest?.(
            '.cl-po--option[data-option] .cl-po--input, .cl-po--option[data-option]'
          );

          if (!node || !this.isSkuNodeForCurrentProduct(node)) return;
          this.processSkuInput(node);
        };

        document.addEventListener('input', this.skuInputSyncHandler, true);
        document.addEventListener('change', this.skuInputSyncHandler, true);

        this.skuInputSyncObserver = new MutationObserver(() => {
          this.scheduleSkuInputScan();
        });

        if (document.body) {
          this.skuInputSyncObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-option'],
          });
        }

        this.skuInputSyncBound = true;
        this.scheduleSkuInputScan();
      }

      teardownSkuInputSync() {
        if (!this.skuInputSyncBound || !this.skuInputSyncHandler) return;

        document.removeEventListener('input', this.skuInputSyncHandler, true);
        document.removeEventListener('change', this.skuInputSyncHandler, true);
        window.clearTimeout(this.skuInputSyncDebounce);
        window.clearTimeout(this.skuInputSyncScanDebounce);
        this.skuInputSyncObserver?.disconnect();
        this.skuInputSyncObserver = null;
        this.skuInputSyncDebounce = null;
        this.skuInputSyncScanDebounce = null;
        this.skuInputSyncHandler = null;
        this.skuInputSyncBound = false;
      }

      scheduleSkuInputScan() {
        window.clearTimeout(this.skuInputSyncScanDebounce);
        this.skuInputSyncScanDebounce = window.setTimeout(() => {
          const nodes = document.querySelectorAll('.cl-po--option[data-option="data-variant-id-sku-map"]');

          for (const node of nodes) {
            if (!this.isSkuNodeForCurrentProduct(node)) continue;
            this.processSkuInput(node);
            break;
          }
        }, 80);
      }

      processSkuInput(input) {
        window.clearTimeout(this.skuInputSyncDebounce);
        this.skuInputSyncDebounce = window.setTimeout(() => {
          const sku = this.resolveSkuFromInput(input);
          if (!sku) return;

          const variantId = this.getVariantIdBySku(sku);
          if (!variantId) return;
          if (`${variantId}` === `${this.dataset.variantId}`) return;

          this.switchVariantById(variantId);
        }, 150);
      }

      isSkuNodeForCurrentProduct(node) {
        const formId = node.getAttribute?.('form');
        if (formId) {
          return (
            formId === `product-form-${this.dataset.section}` ||
            formId === `product-form-installment-${this.dataset.section}`
          );
        }

        if (this.contains(node)) return true;

        const nestedInputWithForm = node.querySelector?.('.cl-po--input[form]');
        const nestedFormId = nestedInputWithForm?.getAttribute?.('form');
        if (!nestedFormId) return false;

        return (
          nestedFormId === `product-form-${this.dataset.section}` ||
          nestedFormId === `product-form-installment-${this.dataset.section}`
        );
      }

      resolveSkuFromInput(node) {
        const mapContainer = node.closest?.('.cl-po--option[data-option="data-variant-id-sku-map"]')
          || (node.matches?.('.cl-po--option[data-option="data-variant-id-sku-map"]') ? node : null);

        const optionNode = node.closest?.('.cl-po--option[data-option]:not([data-option="data-variant-id-sku-map"])');
        const skuFromOptionNode = optionNode?.getAttribute('data-option')?.trim();
        if (skuFromOptionNode) return skuFromOptionNode;

        if (mapContainer) {
          const selectedOptionNode = this.findSelectedSkuOption(mapContainer);
          const skuFromSelectedOptionNode = selectedOptionNode?.getAttribute('data-option')?.trim();
          if (skuFromSelectedOptionNode) return skuFromSelectedOptionNode;
        }

        const skuFromValue = node.value?.trim();
        if (skuFromValue) return skuFromValue;

        return null;
      }

      findSelectedSkuOption(container) {
        const selectedInput = container.querySelector(
          '.cl-po--option[data-option]:not([data-option="data-variant-id-sku-map"]) .cl-po--input:checked'
        );
        if (selectedInput) {
          return selectedInput.closest('.cl-po--option[data-option]:not([data-option="data-variant-id-sku-map"])');
        }

        const selectedOption = container.querySelector(
          '.cl-po--option[data-option]:not([data-option="data-variant-id-sku-map"])[aria-selected="true"], '
            + '.cl-po--option[data-option]:not([data-option="data-variant-id-sku-map"])[aria-checked="true"], '
            + '.cl-po--option[data-option]:not([data-option="data-variant-id-sku-map"]).is-selected, '
            + '.cl-po--option[data-option]:not([data-option="data-variant-id-sku-map"]).selected, '
            + '.cl-po--option[data-option]:not([data-option="data-variant-id-sku-map"]).active'
        );
        if (selectedOption) return selectedOption;

        const options = container.querySelectorAll(
          '.cl-po--option[data-option]:not([data-option="data-variant-id-sku-map"])'
        );
        if (options.length === 1) return options[0];

        return null;
      }

      getVariantIdBySku(sku) {
        const normalizedSku = this.normalizeSku(sku);
        if (!normalizedSku) return null;

        if (!this.skuToVariantIdMap) {
          this.skuToVariantIdMap = this.buildSkuToVariantIdMap();
        }

        return this.skuToVariantIdMap.get(normalizedSku) || null;
      }

      normalizeSku(sku) {
        if (!sku) return '';
        return `${sku}`.trim().toUpperCase();
      }

      buildSkuToVariantIdMap() {
        const map = new Map();

        const skuToVariantScript = this.querySelector('script[data-sku-variant-id-map]');
        if (skuToVariantScript?.textContent?.trim()) {
          try {
            const data = JSON.parse(skuToVariantScript.textContent);
            Object.entries(data).forEach(([sku, variantId]) => {
              const normalizedSku = this.normalizeSku(sku);
              if (!normalizedSku) return;
              map.set(normalizedSku, `${variantId}`);
            });
          } catch (error) {
            console.warn('Failed to parse data-sku-variant-id-map JSON', error);
          }
        }

        if (map.size === 0) {
          const variantToSkuScript = this.querySelector('script[data-variant-id-sku-map]');
          if (variantToSkuScript?.textContent?.trim()) {
            try {
              const data = JSON.parse(variantToSkuScript.textContent);
              Object.entries(data).forEach(([variantId, sku]) => {
                const normalizedSku = this.normalizeSku(sku);
                if (!normalizedSku) return;
                map.set(normalizedSku, `${variantId}`);
              });
            } catch (error) {
              console.warn('Failed to parse data-variant-id-sku-map JSON', error);
            }
          }
        }

        return map;
      }

      switchVariantById(variantId) {
        const nextVariantId = `${variantId}`;
        if (this.pendingSkuVariantSwitchId === nextVariantId) return;

        this.pendingSkuVariantSwitchId = nextVariantId;

        const productUrl = this.dataset.url;
        const requestUrl = `${productUrl}?variant=${encodeURIComponent(nextVariantId)}&section_id=${encodeURIComponent(this.sectionId)}`;

        this.renderProductInfo({
          requestUrl,
          targetId: null,
          callback: this.handleUpdateProductInfo(productUrl),
        }).finally(() => {
          if (`${this.dataset.variantId}` !== nextVariantId) {
            this.pendingSkuVariantSwitchId = null;
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = [
          'price',
          'price-buy-button',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery?.dataset.localizedMedia === 'true') {
          // Localized product galleries use locale-specific metafield images (for example custom.gallery_images_de)
          // and this theme does not currently rely on variant images. Skip variant media reordering/activation so
          // synthetic localized slide IDs are not replaced or forced back to the first matching variant media ID.
          return;
        }

        const mediaGallerySource = mediaGallery?.querySelector('ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          { scrollIntoView: false }
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
